/** @format */
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import { useForm } from "react-hook-form";

import { signUp, useUser } from "@/api/userAPI";
import { type SignUpFormData, signUpSchema } from "@/lib/schemas";
import { setFormErrorsFromApi } from "@/lib/utils/formErrorHandling";
import { trackEvent } from "@/utils/analytics";
import LoadingCircle from "@/utils/LoadingCircle";

const SignUp = ({
  setModalComponent,
  setModalOpen,
}: {
  setModalComponent: (component: string | null) => void;
  setModalOpen: (open: boolean) => void;
}) => {
  const { data: user } = useUser({ enabled: false });
  const navigate = useNavigate();
  const [success, setSuccess] = React.useState(false);

  // Redirect if user is already logged in
  React.useEffect(() => {
    if (user?.id) {
      void navigate({ to: "/", replace: true });
    }
  }, [user?.id, navigate]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email1: "",
      email2: "",
      password1: "",
      password2: "",
      fullName: "",
      shortName: "",
    },
  });

  const onSubmit = async (data: SignUpFormData) => {
    try {
      await signUp(
        data.email1,
        data.password1,
        data.password2,
        data.fullName,
        data.shortName
      );
      trackEvent("signup_completed", { method: "email" });
      setSuccess(true);
    } catch (error: unknown) {
      setFormErrorsFromApi(error, setError, {
        email: "email1",
        password1: "password1",
        password2: "password2",
      });
      console.error(error);
    }
  };

  if (user?.id) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="self-center content-center bg-slate-900 rounded-xl p-4 w-full max-w-2xl">
      <h1 className="text-xl mb-2">Register</h1>
      {success ? (
        <div className="mt-2 bg-slate-900 p-4 rounded">
          <h1 className="text-xl mb-2">Registration Successful!</h1>
          <p className="text-slate-400 mb-2">
            Please check your email for a verification link.
          </p>

          <button
            type="button"
            onClick={() => {
              setModalOpen(true);
              setModalComponent("login");
            }}
            className="w-full flex justify-center my-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Go to Login
          </button>
          <button
            type="button"
            onClick={() => {
              setModalOpen(false);
              setModalComponent(null);
            }}
            className="w-full flex justify-center my-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={e => void handleSubmit(onSubmit)(e)} className="space-y-6">
          <div className="grid grid-cols-2 space-x-2">
            <div className="grow-0">
              <label
                htmlFor="fullName"
                className="block text-sm font-medium text-slate-200"
              >
                Full Name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="fullName"
                  autoComplete="name"
                  required
                  {...register("fullName")}
                  className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                />
              </div>
              {errors.fullName && (
                <div className="text-red-600 whitespace-pre-wrap">
                  {errors.fullName.message}
                </div>
              )}
            </div>
            <div className="grow-0">
              <label
                htmlFor="shortName"
                className="block text-sm font-medium text-slate-200"
              >
                Short Name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="shortName"
                  autoComplete="nickname"
                  required
                  {...register("shortName")}
                  className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                />
              </div>
              {errors.shortName && (
                <div className="text-red-600">{errors.shortName.message}</div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 space-x-2 ">
            <div className="grow-0">
              <label
                htmlFor="email1"
                className="block text-sm font-medium text-slate-200"
              >
                E-Mail
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  id="email1"
                  autoComplete="email"
                  required
                  {...register("email1")}
                  className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                />
              </div>
              {errors.email1 && (
                <div className="text-red-600">{errors.email1.message}</div>
              )}
            </div>
            <div className="grow-0">
              <label
                htmlFor="email2"
                className="block text-sm font-medium text-slate-200"
              >
                Confirm E-Mail
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  id="email2"
                  autoComplete="email"
                  required
                  {...register("email2")}
                  className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                />
              </div>
              {errors.email2 && (
                <div className="text-red-600">{errors.email2.message}</div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 space-x-2">
            <div className="grow-0">
              <label
                htmlFor="password1"
                className="block text-sm font-medium text-slate-200"
              >
                Password
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  id="password1"
                  required
                  {...register("password1")}
                  className="appearance-none block w-full px-3 py-2 border bg-black border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                />
              </div>
              {errors.password1 && (
                <div className="text-red-600">{errors.password1.message}</div>
              )}
            </div>
            <div className="grow-0">
              <label
                htmlFor="password2"
                className="block text-sm font-medium text-slate-200"
              >
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  id="password2"
                  required
                  {...register("password2")}
                  className="appearance-none block w-full px-3 py-2 border bg-black border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                />
              </div>
              {errors.password2 && (
                <div className="text-red-600 whitespace-pre-wrap">
                  {errors.password2.message}
                </div>
              )}
            </div>
          </div>
          <div>
            <button
              type="submit"
              disabled={isSubmitting || Object.keys(errors).length > 0}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <LoadingCircle className="h-5 w-5 mr-2" />
                  Signing Up
                </>
              ) : (
                "Sign Up"
              )}
            </button>
          </div>
          <div className="text-sm text-center text-slate-400">
            <button
              type="button"
              onClick={() => {
                setModalOpen(true);
                setModalComponent("resetPassword");
              }}
              className="font-medium text-cPurple-600 hover:text-cPurple-500"
            >
              Forgot your password?
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SignUp;
